import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure, type PortalFailure } from "../lib/errors";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The client's own half of "request user changes": plan.md section 4, which gives the right to the
 * client authorised contact by name and gives it to nobody else.
 *
 * `public.request_membership_change` has been granted to `authenticated` since migration 2 and had
 * no caller anywhere in the product until this file. Migration 57 built Petrovic's side, so the
 * board could be read and nothing could put a row on it.
 *
 * **This asks and never grants.** plan.md section 4: "There is no public registration. Petrovic
 * creates or approves every membership." The gateway writes a row into
 * `private.membership_invitations` and returns its id; the membership itself is created by hand with
 * `public.admin_create_membership`. Nothing here changes who can sign in, and the panel says so
 * rather than letting a person expect otherwise.
 *
 * **The person does not have to be a contact yet.** Until 2026-09-13
 * `membership_invitations.contact_id` was `not null`, so the form could only name somebody Petrovic
 * had already recorded, and the one case the panel exists for, a new colleague, had no shape the
 * gateway would take. A request now names a contact or carries a name and an address, and Petrovic's
 * approval is what records the person. That is still the no-public-registration rule: nothing here
 * creates a contact, a membership or an account.
 *
 * **The address is sent as typed.** `public.request_membership_change` lowercases it itself, which
 * migration 46 does in the same statement as the insert. Normalising here as well would be two
 * implementations of one rule, and the one that drifts is the one nobody is looking at.
 *
 * **The reads are direct and the write is a gateway**, which is architecture.md section 3B and
 * plan.md 14.2. `public.contacts` and `public.memberships` are both granted to `authenticated`
 * column by column with tenant-scoped policies, so the picker needs nothing built for it. The
 * request history is not: `private.membership_invitations` is unreachable from any browser role, so
 * `public.my_membership_requests` exists to answer for it.
 */

export const tenantPeopleKey = (tenantId: string) => ["portal", "tenant-people", tenantId] as const;
export const membershipRequestsKey = (tenantId: string) =>
  ["portal", "membership-requests", tenantId] as const;

/** The two roles `memberships_role_check` and `membership_invitations_role_check` both allow. */
export type MembershipRole = "client_authorised_contact" | "client_member";

/**
 * Every ending `public.my_membership_requests` can report, and there are four rather than five for a
 * reason worth knowing before reading the panel.
 *
 * `answered` covers both of Petrovic's answers. `public.admin_resolve_membership_request` writes
 * `revoked_at` whether he granted the request or declined it, which migration 57 decided
 * deliberately: the schema has one column for "resolved without being accepted" and the reason lives
 * in an audit row no client may read. So the portal cannot tell the two apart and does not guess.
 * The access list above the history is where the answer actually shows, because a membership that
 * was created is a login somebody now has.
 *
 * `expired` covers the scheduled driver closing a request at its own date, and the rarer case of
 * Petrovic answering more than fourteen days late. Both are answered by asking again.
 *
 * `approved` is Petrovic's yes before the operations tick has sent the invitation, and `granted` is
 * the membership existing and the invitation sent, which is what `accepted_at` records since
 * migration `20260913000002`.
 */
export type MembershipRequestOutcome = "open" | "approved" | "granted" | "answered" | "expired";

export interface TenantPerson {
  contactId: string;
  fullName: string;
  email: string;
  jobTitle: string | null;
  /** The role this person already holds in the tenant, or null when they have no login. */
  currentRole: MembershipRole | null;
  /** `invited`, `active`, `suspended` or `disabled`. Null when there is no membership row at all. */
  membershipStatus: string | null;
}

export interface MembershipRequest {
  invitation_id: string;
  /** Null for a new person Petrovic has not approved yet. */
  contact_id: string | null;
  contact_name: string;
  email: string;
  role: string;
  requested_at: string;
  expires_at: string;
  outcome: MembershipRequestOutcome;
}

/**
 * The tenant's people, each with the access they have today.
 *
 * Two reads rather than one, joined here, because the portal has no view that joins them and adding
 * one would be a migration for a screen. Both are ordinary tenant-scoped selects the policies
 * already allow, so the join is arithmetic rather than authority: a contact this caller may not read
 * is absent from the first result and a membership they may not read is absent from the second.
 *
 * Inactive contacts are filtered out. `contacts.active` is false for somebody who has left, and
 * offering to request access for them would be offering to undo a decision this screen cannot see
 * the reason for.
 */
export function useTenantPeople(tenantId: string | null): UseQueryResult<TenantPerson[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, tenantPeopleKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        // The gateway already orders by full_name in the database's collation, as the client's
        // `.order("full_name")` does, so only the active filter is applied here.
        const [contactRows, membershipRows] = await Promise.all([
          viewRows<{ id: string; full_name: string; email: string; job_title: string | null; active: boolean }>(
            "admin_as_client_contacts",
            { p_tenant_id: source.tenantId }
          ),
          viewRows<{ contact_id: string; role: string; status: string }>("admin_as_client_memberships", {
            p_tenant_id: source.tenantId
          })
        ]);
        const byContact = new Map(
          membershipRows.map(({ contact_id, role, status }) => [contact_id, { contact_id, role, status }])
        );

        return contactRows
          .filter((contact) => contact.active)
          .map((contact) => {
            const membership = byContact.get(contact.id);

            return {
              contactId: contact.id,
              fullName: contact.full_name ?? "",
              email: contact.email ?? "",
              jobTitle: contact.job_title ?? null,
              currentRole: (membership?.role as MembershipRole | undefined) ?? null,
              membershipStatus: membership?.status ?? null
            } satisfies TenantPerson;
          });
      }
      const supabase = getSupabaseBrowserClient();

      const contacts = await supabase
        .from("contacts")
        .select("id, full_name, email, job_title, active")
        .eq("tenant_id", tenantId as string)
        .eq("active", true)
        .order("full_name");

      if (contacts.error) {
        throw contacts.error;
      }

      const memberships = await supabase
        .from("memberships")
        .select("contact_id, role, status")
        .eq("tenant_id", tenantId as string);

      if (memberships.error) {
        throw memberships.error;
      }

      const byContact = new Map(
        (memberships.data ?? []).map((row) => [row.contact_id as string, row])
      );

      return (contacts.data ?? []).map((contact) => {
        const membership = byContact.get(contact.id as string);

        return {
          contactId: contact.id as string,
          fullName: (contact.full_name as string) ?? "",
          email: (contact.email as string) ?? "",
          jobTitle: (contact.job_title as string | null) ?? null,
          currentRole: (membership?.role as MembershipRole | undefined) ?? null,
          membershipStatus: (membership?.status as string | undefined) ?? null
        } satisfies TenantPerson;
      });
    }
  });
}

/**
 * What this tenant has asked for and how each request ended.
 *
 * The gateway refuses anybody who is not the authorised contact, so this is fetched only for a
 * caller the panel has already established is one. Fetching it for a member would produce a
 * `PORTAL_FORBIDDEN_ROLE` the screen has nothing to do with: the member is not being refused
 * anything, they are simply reading a panel that has a different shape for them.
 */
export function useMembershipRequests(
  tenantId: string | null,
  enabled: boolean
): UseQueryResult<MembershipRequest[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, membershipRequestsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId) && enabled,
    queryFn: async () => {
      // The requests are personal to the one authorised contact, and the view leaves that panel out.
      if (source.kind === "admin") {
        return [];
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("my_membership_requests", {
        p_tenant_id: tenantId as string
      });

      if (error) {
        throw error;
      }

      return (data ?? []) as MembershipRequest[];
    }
  });
}

export interface MembershipChangeInput {
  tenantId: string;
  /** An existing contact, or null for a new person named by `fullName` and `email`. */
  contactId: string | null;
  fullName: string | null;
  email: string;
  role: MembershipRole;
  /** The raw step-up token. The database holds only its hash and spends the grant on this call. */
  stepUpToken: string;
}

/**
 * Asks for one access change, spending one step-up grant.
 *
 * The grant is consumed inside `public.request_membership_change`, in the same transaction as the
 * insert, which is plan.md 14.3's rule: "A grant that is read and left in place is a replay window."
 * So this mutation cannot succeed without one and cannot leave one behind.
 *
 * `PORTAL_MEMBERSHIP_REQUEST_ALREADY_OPEN` arrives here when the same person already has a request
 * open inside its fourteen days. The gateway checks it before the rate limit and before the grant is
 * spent, so a second press costs the caller neither and the step-up they just proved is still theirs
 * to spend on something else.
 */
export function useRequestMembershipChange() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<string, PortalFailure, MembershipChangeInput>({
    mutationFn: async ({ tenantId, contactId, fullName, email, role, stepUpToken }) => {
      refuseInView(source);
      const { data, error } = await getSupabaseBrowserClient().rpc("request_membership_change", {
        p_tenant_id: tenantId,
        // The generated type says `string`, because PostgREST reads a nullable argument's type and
        // not its nullability. The gateway takes null for a new person and says why on its face.
        p_contact_id: contactId as string,
        p_email: email,
        p_role: role,
        p_step_up_token: stepUpToken,
        p_full_name: fullName ?? undefined
      });

      if (error) {
        throw toPortalFailure(error);
      }

      return (data as string) ?? "";
    },
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: membershipRequestsKey(variables.tenantId) })
  });
}
